import React, { useState, useCallback, useRef, useEffect } from 'react';
import SuperAideyChat from '../SuperAideyChat';
import HelpButton from '../../tours/HelpButton';
import useAutoTour from '../../tours/useAutoTour';
import { api } from '../../api/client';
import { loadSessions, deleteSession, deleteAllSessions, newSessionId, saveSession, loadSessionsFromDb, deleteSessionFromDb, deleteAllSessionsFromDb, renameSessionInDb } from '../../services/aideySessionsService';

const NAVY   = '#0C2D48';
const YELLOW = '#E4E69B';
const BORDER = '#35454C';

const PROJECT_ICONS   = ['💬', '📋', '💼', '📧', '💡', '🎯', '📊', '🔧', '🤝', '📝', '🚀', '⭐'];
const PROJECT_COLORS  = ['#0C2D48', '#27ae60', '#e67e22', '#8e44ad', '#c0392b', '#16a085'];

// ── Helpers ────────────────────────────────────────────────────────────────────
function groupLabel(isoDate) {
  const d = new Date(isoDate);
  const diffDays = Math.floor((Date.now() - d) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7)  return 'Last 7 days';
  if (diffDays < 30) return 'Last 30 days';
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

function groupSessions(sessions) {
  const groups = {};
  for (const s of sessions) {
    const label = groupLabel(s.updatedAt || s.createdAt);
    if (!groups[label]) groups[label] = [];
    groups[label].push(s);
  }
  return Object.entries(groups);
}

function relativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMins = Math.floor((Date.now() - d) / 60000);
  if (diffMins < 1)  return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffH = Math.floor(diffMins / 60);
  if (diffH < 24)    return `${diffH}h ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function mergeSessions(dbSessions, localSessions) {
  const dbIds = new Set(dbSessions.map(s => s.id));
  const localOnly = localSessions.filter(s => !dbIds.has(s.id));
  return [...dbSessions, ...localOnly].sort((a, b) =>
    new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function AideyChatDashboard({ onCreateTask, userSchedule, onMoveTask, onCancelTask, activeWorkspaceId }) {
  useAutoTour('aidey-chat');
  const [sessions, setSessions]           = useState(() => loadSessions());
  const [projects, setProjects]           = useState([]);
  const [selectedView, setSelectedView]   = useState('all'); // 'all' | 'unorganised' | <projectId>
  const [activeId, setActiveId]           = useState(null);
  const [activeSessionId, setActiveSessionId] = useState(() => newSessionId());
  const [hoveredId, setHoveredId]         = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [renamingId, setRenamingId]       = useState(null);
  const [renameValue, setRenameValue]     = useState('');
  const [sidebarOpen, setSidebarOpen]     = useState(false);
  const renameInputRef                    = useRef(null);
  const [hoveredProjectId, setHoveredProjectId] = useState(null);
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);
  const [newProjectName, setNewProjectName]   = useState('');
  const [newProjectIcon, setNewProjectIcon]   = useState('💬');
  const [newProjectColor, setNewProjectColor] = useState('#0C2D48');
  const [savingProject, setSavingProject]     = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await api('/api/aidey-projects');
      if (res.ok) { const d = await res.json(); setProjects(d.projects || []); }
    } catch {}
  }, []);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    // Clear previous workspace's sessions immediately so stale data never shows
    setSessions([]);
    setActiveId(null);
    setActiveSessionId(newSessionId());
    loadSessionsFromDb().then(dbSessions => {
      if (!dbSessions.length) return;
      const merged = mergeSessions(dbSessions, loadSessions());
      setSessions(merged);
      const recent = merged[0];
      if (recent) {
        const diffH = (Date.now() - new Date(recent.updatedAt || recent.createdAt).getTime()) / 3600000;
        if (diffH < 24) { setActiveId(recent.id); setActiveSessionId(recent.id); }
      }
    });
    fetchProjects();
  }, [activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSessionUpdated = useCallback(() => {
    loadSessionsFromDb().then(dbSessions => {
      setSessions(mergeSessions(dbSessions, loadSessions()));
    }).catch(() => {
      setSessions(loadSessions());
    });
  }, []);

  function startNewChat() {
    setActiveId(null);
    setActiveSessionId(newSessionId());
  }

  function openSession(id) {
    setActiveId(id);
    setActiveSessionId(id);
    setSidebarOpen(false);
  }

  function handleDelete(e, id) {
    e.stopPropagation();
    if (confirmDeleteId === id) {
      deleteSession(id);
      deleteSessionFromDb(id);
      setSessions(prev => prev.filter(s => s.id !== id));
      if (activeId === id) startNewChat();
      setConfirmDeleteId(null);
    } else {
      setConfirmDeleteId(id);
    }
  }

  function handleClearAll() {
    if (window.confirm('Delete all Aidey chat history? This cannot be undone.')) {
      deleteAllSessions();
      deleteAllSessionsFromDb();
      setSessions([]);
      startNewChat();
    }
  }

  function startRename(e, session) {
    e.stopPropagation();
    setRenamingId(session.id);
    setRenameValue(session.title || '');
    setTimeout(() => renameInputRef.current?.focus(), 0);
  }

  function commitRename(session) {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== session.title) {
      saveSession({ ...session, title: trimmed });
      renameSessionInDb(session.id, trimmed);
      setSessions(prev => prev.map(s => s.id === session.id ? { ...s, title: trimmed } : s));
    }
    setRenamingId(null);
  }

  async function handleAssignProject(e, sessionId, newProjectId, currentProjectId) {
    e.stopPropagation();
    try {
      if (newProjectId) {
        await api(`/api/aidey-projects/${newProjectId}/sessions/${sessionId}`, { method: 'POST' });
      } else if (currentProjectId) {
        await api(`/api/aidey-projects/${currentProjectId}/sessions/${sessionId}`, { method: 'DELETE' });
      }
      setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, projectId: newProjectId || null } : s));
      fetchProjects();
    } catch {}
  }

  async function handleCreateProject(e) {
    e.preventDefault();
    if (!newProjectName.trim()) return;
    setSavingProject(true);
    try {
      const res = await api('/api/aidey-projects', {
        method: 'POST',
        body: JSON.stringify({ name: newProjectName.trim(), icon: newProjectIcon, color: newProjectColor }),
      });
      if (res.ok) {
        const d = await res.json();
        setProjects(prev => [{ ...d.project, sessionCount: 0 }, ...prev]);
        setShowNewProjectForm(false);
        setNewProjectName('');
        setNewProjectIcon('💬');
        setNewProjectColor('#0C2D48');
      }
    } catch {}
    setSavingProject(false);
  }

  async function handleDeleteProject(e, projectId) {
    e.stopPropagation();
    if (!window.confirm('Delete this project? Your chats will not be deleted.')) return;
    try {
      await api(`/api/aidey-projects/${projectId}`, { method: 'DELETE' });
      setProjects(prev => prev.filter(p => p.id !== projectId));
      setSessions(prev => prev.map(s => s.projectId === projectId ? { ...s, projectId: null } : s));
      if (selectedView === projectId) setSelectedView('all');
    } catch {}
  }

  const filteredSessions = sessions.filter(s => {
    if (selectedView === 'all') return true;
    if (selectedView === 'unorganised') return !s.projectId;
    return s.projectId === selectedView;
  });

  const grouped = groupSessions(filteredSessions);
  const currentTitle = activeId ? (sessions.find(s => s.id === activeId)?.title || 'Chat') : 'New Chat';
  const projectMap = Object.fromEntries(projects.map(p => [p.id, p]));

  return (
    <div className="aidey-chat-layout" style={{
      display: 'grid',
      gap: '1.25rem',
      fontFamily: "'Raleway', sans-serif",
    }}>
      {sidebarOpen && (
        <div className="aidey-chat-sidebar-overlay is-visible" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Left panel ─────────────────────────────────────────────── */}
      <div className={`aidey-chat-sidebar${sidebarOpen ? ' is-open' : ''}`} style={{
        background: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '1rem 1rem 0.75rem', borderBottom: '1px solid #efefef', flexShrink: 0 }}>
          <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.95rem', color: NAVY, marginBottom: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              Aidey Chat
              <HelpButton tourId="aidey-chat" />
            </div>
            <button
              className="aidey-sidebar-close-btn"
              onClick={() => setSidebarOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '1.1rem', padding: '0.25rem 0.4rem', lineHeight: 1 }}
            >
              ✕
            </button>
          </div>
          <button
            data-tour-id="chat-new-chat"
            onClick={startNewChat}
            style={{ width: '100%', padding: '0.5rem 0.75rem', background: NAVY, color: YELLOW, border: 'none', borderRadius: 8, fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', justifyContent: 'center' }}
          >
            <span style={{ fontSize: '1rem', lineHeight: 1 }}>+</span> New Chat
          </button>
        </div>

        {/* Scrollable body */}
        <div data-tour-id="chat-history" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

          {/* Projects section */}
          <div style={{ padding: '0.75rem 1rem 0.4rem', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Projects
              </div>
              <button
                onClick={() => setShowNewProjectForm(f => !f)}
                title="New project"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', fontSize: '1.1rem', lineHeight: 1, padding: '0 2px', fontWeight: 700 }}
              >
                +
              </button>
            </div>

            {/* Inline new-project form */}
            {showNewProjectForm && (
              <form onSubmit={handleCreateProject} style={{ background: '#f8f9fb', borderRadius: 8, padding: '0.65rem', marginBottom: '0.5rem', border: '1px solid #e8e8e8' }}>
                <input
                  value={newProjectName}
                  onChange={e => setNewProjectName(e.target.value)}
                  placeholder="Project name"
                  maxLength={50}
                  autoFocus
                  style={{ width: '100%', border: 'none', borderBottom: `1.5px solid ${NAVY}`, outline: 'none', fontSize: '0.82rem', fontFamily: "'Raleway', sans-serif", background: 'transparent', color: NAVY, padding: '0 0 3px', marginBottom: '0.5rem', boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', gap: '0.2rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
                  {PROJECT_ICONS.map(ic => (
                    <button key={ic} type="button" onClick={() => setNewProjectIcon(ic)}
                      style={{ background: newProjectIcon === ic ? '#e8f0fb' : 'none', border: `1.5px solid ${newProjectIcon === ic ? NAVY : 'transparent'}`, borderRadius: 4, cursor: 'pointer', fontSize: '1rem', padding: '1px 3px', lineHeight: 1 }}>
                      {ic}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.5rem' }}>
                  {PROJECT_COLORS.map(c => (
                    <button key={c} type="button" onClick={() => setNewProjectColor(c)}
                      style={{ width: 18, height: 18, borderRadius: '50%', background: c, border: `2px solid ${newProjectColor === c ? '#333' : 'transparent'}`, cursor: 'pointer', padding: 0, flexShrink: 0 }} />
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '0.3rem' }}>
                  <button type="submit" disabled={!newProjectName.trim() || savingProject}
                    style={{ flex: 1, background: NAVY, color: YELLOW, border: 'none', borderRadius: 6, fontSize: '0.75rem', fontWeight: 700, padding: '0.3rem', cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }}>
                    {savingProject ? '…' : 'Create'}
                  </button>
                  <button type="button" onClick={() => { setShowNewProjectForm(false); setNewProjectName(''); }}
                    style={{ flex: 1, background: 'none', border: '1px solid #e0e0e0', borderRadius: 6, fontSize: '0.75rem', color: '#888', padding: '0.3rem', cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }}>
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {/* Project list */}
            {projects.length === 0 && !showNewProjectForm && (
              <p style={{ fontSize: '0.75rem', color: '#ccc', margin: '0.25rem 0 0.5rem', lineHeight: 1.4 }}>
                No projects yet. Use + to create one.
              </p>
            )}
            {projects.map(p => {
              const isActive = selectedView === p.id;
              const isHov = hoveredProjectId === p.id;
              return (
                <div key={p.id}
                  onClick={() => setSelectedView(p.id)}
                  onMouseEnter={() => setHoveredProjectId(p.id)}
                  onMouseLeave={() => setHoveredProjectId(null)}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.32rem 0.4rem', borderRadius: 6, cursor: 'pointer', background: isActive ? '#f0f4f8' : isHov ? '#f8f9fa' : 'transparent', marginBottom: '0.1rem', transition: 'background 0.1s' }}
                >
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: p.color || NAVY, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.85rem' }}>{p.icon}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: '0.8rem', fontWeight: isActive ? 700 : 500, color: isActive ? NAVY : '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.name}
                  </span>
                  <span style={{ fontSize: '0.7rem', color: '#ccc', flexShrink: 0 }}>{p.sessionCount || 0}</span>
                  {isHov && (
                    <button onClick={e => handleDeleteProject(e, p.id)} title="Delete project"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: '0.7rem', padding: '0 1px', lineHeight: 1, flexShrink: 0 }}>
                      ✕
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* All chats / Unorganised */}
          <div style={{ borderTop: '1px solid #f0f0f0', padding: '0.5rem 1rem 0.35rem', flexShrink: 0 }}>
            {[
              { id: 'all',          label: 'All chats',    icon: '💬' },
              { id: 'unorganised',  label: 'Unorganised',  icon: '📂' },
            ].map(item => {
              const isActive = selectedView === item.id;
              return (
                <div key={item.id}
                  onClick={() => setSelectedView(item.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', padding: '0.32rem 0.4rem', borderRadius: 6, cursor: 'pointer', background: isActive ? '#f0f4f8' : 'transparent', borderLeft: isActive ? `3px solid ${NAVY}` : '3px solid transparent', marginBottom: '0.12rem', transition: 'background 0.1s' }}
                >
                  <span style={{ fontSize: '0.82rem' }}>{item.icon}</span>
                  <span style={{ fontSize: '0.82rem', fontWeight: isActive ? 700 : 500, color: isActive ? NAVY : '#555' }}>{item.label}</span>
                </div>
              );
            })}
          </div>

          {/* Session list */}
          <div style={{ flex: 1 }}>
            {filteredSessions.length === 0 && (
              <p style={{ padding: '1.5rem 1rem', fontSize: '0.8rem', color: '#bbb', textAlign: 'center', lineHeight: 1.5 }}>
                {selectedView === 'unorganised'
                  ? 'All chats are in a project.'
                  : selectedView !== 'all'
                    ? 'No chats in this project yet.'
                    : 'Start a conversation — it\'ll appear here so you can pick it up later.'}
              </p>
            )}

            {grouped.map(([label, groupItems]) => (
              <div key={label}>
                <div style={{ padding: '0.55rem 1rem 0.2rem', fontSize: '0.68rem', fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {label}
                </div>

                {groupItems.map(session => {
                  const isActive     = session.id === activeId;
                  const isHovered    = session.id === hoveredId;
                  const isConfirming = session.id === confirmDeleteId;
                  const isRenaming   = session.id === renamingId;
                  const isOld = (Date.now() - new Date(session.updatedAt || session.createdAt).getTime()) > 30 * 86400000;
                  const proj  = session.projectId ? projectMap[session.projectId] : null;

                  return (
                    <div
                      key={session.id}
                      onClick={e => { if (e.target.closest('select, input, button')) return; if (!isRenaming) openSession(session.id); }}
                      onMouseEnter={() => setHoveredId(session.id)}
                      onMouseLeave={() => { setHoveredId(null); setConfirmDeleteId(null); }}
                      style={{ padding: '0.55rem 0.75rem 0.55rem 1rem', cursor: isRenaming ? 'default' : 'pointer', background: isActive ? '#f0f4f8' : isHovered ? '#f8f9fa' : 'transparent', borderLeft: isActive ? `3px solid ${NAVY}` : '3px solid transparent', display: 'flex', alignItems: 'flex-start', gap: '0.4rem', transition: 'background 0.12s', position: 'relative' }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {isRenaming ? (
                          <input
                            ref={renameInputRef}
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            onBlur={() => commitRename(session)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitRename(session); } if (e.key === 'Escape') setRenamingId(null); }}
                            onClick={e => e.stopPropagation()}
                            style={{ width: '100%', border: 'none', borderBottom: `1.5px solid ${NAVY}`, outline: 'none', fontSize: '0.82rem', fontWeight: 600, fontFamily: "'Raleway', sans-serif", background: 'transparent', color: NAVY, padding: '0 0 1px' }}
                          />
                        ) : (
                          <div style={{ fontSize: '0.82rem', fontWeight: isActive ? 700 : 500, color: isActive ? NAVY : isOld ? '#bbb' : '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: isOld ? 0.7 : 1 }}>
                            {session.title || 'Untitled'}
                          </div>
                        )}

                        {/* Meta row */}
                        <div style={{ fontSize: '0.68rem', color: '#bbb', marginTop: 2, display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap' }}>
                          <span>{relativeTime(session.updatedAt)}</span>
                          <span>·</span>
                          <span>{session.messageCount ?? session.messages?.filter(m => m.role === 'user').length ?? 0} msg</span>
                          {/* Project badge — only when viewing "all" */}
                          {proj && selectedView === 'all' && (
                            <span style={{ padding: '0 5px', borderRadius: 3, background: proj.color || NAVY, color: '#fff', fontSize: '0.64rem', fontWeight: 600, lineHeight: '15px', flexShrink: 0 }}>
                              {proj.icon} {proj.name}
                            </span>
                          )}
                        </div>

                        {/* Project assignment dropdown — visible on hover */}
                        {(isHovered || isActive) && !isRenaming && (
                          <select
                            value={session.projectId || ''}
                            onClick={e => e.stopPropagation()}
                            onChange={e => handleAssignProject(e, session.id, e.target.value || null, session.projectId)}
                            style={{ marginTop: '0.3rem', fontSize: '0.72rem', border: '1px solid #e0e0e0', borderRadius: 4, background: '#fff', color: '#666', padding: '1px 3px', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", maxWidth: '100%' }}
                          >
                            <option value="">No project</option>
                            {projects.map(p => (
                              <option key={p.id} value={p.id}>{p.icon} {p.name}</option>
                            ))}
                          </select>
                        )}
                      </div>

                      {/* Rename + Delete */}
                      {(isHovered || isActive) && !isRenaming && (
                        <div style={{ display: 'flex', gap: '0.2rem', flexShrink: 0, marginTop: 1 }}>
                          <button onClick={e => startRename(e, session)} title="Rename chat"
                            style={{ background: 'transparent', border: 'none', borderRadius: 4, color: '#bbb', cursor: 'pointer', fontSize: '0.72rem', padding: '0.15rem 0.3rem', lineHeight: 1, fontFamily: "'Raleway', sans-serif" }}>
                            ✎
                          </button>
                          <button onClick={e => handleDelete(e, session.id)} title={isConfirming ? 'Click again to confirm delete' : 'Delete chat'}
                            style={{ flexShrink: 0, background: isConfirming ? '#fce8e8' : 'transparent', border: isConfirming ? '1px solid #f5c6c6' : 'none', borderRadius: 4, color: isConfirming ? '#e55' : '#ccc', cursor: 'pointer', fontSize: '0.75rem', padding: '0.15rem 0.35rem', fontFamily: "'Raleway', sans-serif", lineHeight: 1 }}>
                            {isConfirming ? 'Sure?' : '✕'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        {sessions.length > 0 && (
          <div style={{ padding: '0.65rem 1rem', borderTop: '1px solid #f0f0f0', flexShrink: 0 }}>
            <button onClick={handleClearAll}
              style={{ width: '100%', padding: '0.4rem', background: 'none', border: '1px solid #fcc', borderRadius: 6, color: '#E96A6A', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }}>
              Clear all history
            </button>
          </div>
        )}
      </div>

      {/* ── Right panel ────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Title bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', paddingLeft: '0.25rem' }}>
          <button
            className="aidey-chat-menu-btn"
            onClick={() => setSidebarOpen(true)}
            style={{ background: '#f0f4f8', border: '1px solid #dde6ef', borderRadius: 7, cursor: 'pointer', color: NAVY, fontSize: '0.82rem', fontWeight: 700, padding: '0.4rem 0.75rem', lineHeight: 1, fontFamily: "'Raleway', sans-serif", flexShrink: 0 }}
          >
            ☰ Chats
          </button>
          <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem', color: NAVY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
            {currentTitle}
          </div>
          {activeId && (
            <button onClick={startNewChat}
              style={{ padding: '0.3rem 0.75rem', background: YELLOW, border: 'none', borderRadius: 6, fontWeight: 700, fontSize: '0.78rem', color: NAVY, cursor: 'pointer', fontFamily: "'Raleway', sans-serif", flexShrink: 0 }}>
              + New Chat
            </button>
          )}
        </div>

        {/* Chat */}
        <div data-tour-id="chat-messages" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <SuperAideyChat
            key={activeSessionId}
            onCreateTask={onCreateTask}
            userSchedule={userSchedule}
            onMoveTask={onMoveTask}
            onCancelTask={onCancelTask}
            isPopup={false}
            fullPage={true}
            sessionId={activeSessionId}
            onSessionUpdated={handleSessionUpdated}
          />
        </div>
      </div>
    </div>
  );
}
